import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  Bot,
  Users,
  Heart,
  CheckCircle,
  Target,
  Shield,
  Zap,
  Star,
  Code2,
  Globe,
} from 'lucide-react';
import { setSeoMetadata, generateOrganizationSchema } from '../lib/seo';
import Header from '../components/Header';
import Footer from '../components/Footer';

const AboutUsPage = () => {
  useEffect(() => {
    setSeoMetadata({
      title: 'About Us',
      description: 'AI Joomla is built and maintained by A2G Designs, a Tampa, Florida based agency with 20+ years of experience in web design, full stack software development, and hosting for businesses nationwide.',
      keywords: 'AI Joomla about us, A2G Designs, Joomla extension developers, full stack development agency, Tampa web design company',
      canonicalPath: '/about',
      ogType: 'website',
      jsonLd: generateOrganizationSchema(),
    });
  }, []);

  const values = [
    {
      icon: Shield,
      title: 'Honest, Upfront Pricing',
      description: 'Free estimates before any work begins. No surprises, no hidden fees — on websites, software, or Joomla extensions.',
    },
    {
      icon: Target,
      title: 'Relentless Solution-Orientation',
      description: 'We solve technical challenges with urgency, whether it is a client website or a Joomla security patch.',
    },
    {
      icon: Users,
      title: 'Human-Centric Craftsmanship',
      description: 'Every extension and website is built and supported by real in-house engineers, never outsourced contractors.',
    },
    {
      icon: Zap,
      title: 'Full Stack Excellence',
      description: 'From websites and CRMs to Joomla extensions, we engineer scalable software that performs.',
    },
  ];

  const differentiators = [
    '100% In-House: None of our design, development, or support work is outsourced or farmed out to contractors — it is all built right here.',
    '20+ Years of Experience: Two decades of web design, SEO, and hosting experience power every AI Joomla extension we ship.',
    'We Build Websites and Full Stack Applications: From AI-powered platforms to custom CRM systems and Joomla extensions, we deliver enterprise-quality software at an affordable cost.',
    'A+ BBB Rating: Trusted nationally with an A+ Better Business Bureau rating, and organic, white-hat SEO results — no paid ads required.',
    '40+ Servers Nationwide: Enterprise-grade hosting infrastructure with a 99.9% uptime guarantee backs every product and update we ship.',
  ];

  const milestones = [
    { year: '20+ Years Ago', event: 'A2G Designs Founded. Began as a Tampa, Florida based web design and hosting company, serving small businesses with custom websites and digital marketing.' },
    { year: 'Scaling Up', event: 'Software & Full Stack Development. Expanded our in-house capabilities to build scalable software solutions — from AI-powered platforms to custom CRM systems — for clients nationwide.' },
    { year: 'Entering Joomla', event: 'AI Joomla Founded. Applied two decades of full stack engineering experience to the Joomla ecosystem, building extensions in-house rather than outsourcing them.' },
    { year: 'Today', event: 'AI Joomla Security Suite. Launched a fully comprehensive security suite for Joomla 4, 5, and 6, backed by the same reliability and support standards A2G Designs clients have trusted for 20+ years.' },
  ];

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="about-us-page">
      <Header />
      <div className="bg-[#0a1628] text-white py-20 pt-36">
        <div className="max-w-6xl mx-auto px-4">
          <Link to="/" className="inline-flex items-center text-blue-400 hover:text-white mb-6" data-testid="about-us-back-home-link">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6" data-testid="about-us-hero-title">
                Our Story,
                <br />
                <span className="text-green-400">Built by A2G Designs.</span>
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed" data-testid="about-us-hero-summary">
                AI Joomla is proudly built and maintained by A2G Designs — a Tampa, Florida based agency with 20+ years of experience building websites, full stack applications, and enterprise-grade software for businesses nationwide.
              </p>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-64 h-64 rounded-full bg-gradient-to-br from-blue-500/30 to-green-500/20 flex items-center justify-center">
                  <Bot className="w-32 h-32 text-blue-400" />
                </div>
                <div className="absolute -top-4 -right-4 w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Star className="w-10 h-10 text-blue-400" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-green-500/30 flex items-center justify-center">
                  <Code2 className="w-8 h-8 text-green-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <section data-testid="about-us-story-section">
            <h2 className="text-3xl font-bold text-white mb-6">Our Story</h2>
            <div className="space-y-4 text-gray-300 leading-relaxed">
              <p>
                A2G Designs is an award-winning web design and software development agency based in Tampa, Florida, serving businesses across all 50 states. For over 20 years, our in-house team has delivered custom web design, managed hosting, local SEO, VoIP services, and full-color printing to thousands of small businesses nationwide.
              </p>
              <p>
                Beyond websites, <strong className="text-white">we build websites and full stack applications</strong> — scalable software solutions that transform businesses, from AI-powered platforms to custom CRM systems, all delivered at enterprise quality and an affordable cost.
              </p>
              <p>
                AI Joomla was born from that same engineering discipline. We took our full stack development expertise and applied it to the Joomla ecosystem, building the AI Joomla Security Suite and a growing catalog of extensions engineered in-house for Joomla 4, 5, and 6.
              </p>
              <p>
                Every extension we ship carries the same promise we have made to A2G Designs clients for two decades: reliable engineering, honest pricing, and real support from the people who actually wrote the code.
              </p>
            </div>
          </section>

          <div className="bg-[#111d2e] rounded-2xl p-8 border border-gray-700" data-testid="about-us-differentiators-section">
            <h3 className="text-xl font-bold text-white mb-4">What Sets Us Apart</h3>
            <ul className="space-y-3">
              {differentiators.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3" data-testid={`about-us-differentiator-${idx + 1}`}>
                  <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                  <span className="text-gray-300 leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-[#111d2e] py-16" data-testid="about-us-core-values-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Core Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => {
              const Icon = value.icon;
              return (
                <div key={idx} className="bg-[#0a1628] backdrop-blur rounded-xl p-6 text-center border border-gray-700" data-testid={`about-us-core-value-${idx + 1}`}>
                  <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{value.title}</h3>
                  <p className="text-gray-400 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16" data-testid="about-us-journey-section">
        <h2 className="text-3xl font-bold text-white text-center mb-12">Our Journey</h2>
        <div className="relative">
          <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gradient-to-b from-blue-500 to-green-500" />

          <div className="space-y-8">
            {milestones.map((milestone, idx) => (
              <div key={idx} className={`flex items-center gap-8 ${idx % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`} data-testid={`about-us-journey-item-${idx + 1}`}>
                <div className={`flex-1 ${idx % 2 === 0 ? 'text-right' : 'text-left'}`}>
                  <div className="bg-[#111d2e] rounded-xl p-6 shadow-lg inline-block max-w-md border border-gray-700">
                    <span className="text-blue-400 font-bold text-lg">{milestone.year}</span>
                    <p className="text-gray-300 mt-2 leading-relaxed">{milestone.event}</p>
                  </div>
                </div>
                <div className="w-4 h-4 rounded-full bg-green-400 ring-4 ring-[#0a1628] shadow-lg z-10" />
                <div className="flex-1" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Where We're Based Section */}
      <div className="bg-[#0a1628] py-16 border-t border-gray-800" data-testid="about-us-service-area-section">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">
            <span className="text-green-400 italic">TAMPA, FLORIDA</span>{' '}
            <span className="text-white">BASED — SERVING ALL 50 STATES</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto mb-8">
            AI Joomla is a product of A2G Designs, a full-service web design, hosting, and software development agency. Whether you need a Joomla extension, a custom website, or a full stack application built from scratch, our in-house team handles it all.
          </p>
          <a
            href="https://a2gdesigns.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-blue-400 hover:text-white font-semibold"
            data-testid="about-us-a2g-designs-link"
          >
            <Globe className="w-5 h-5" /> Visit A2G Designs
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutUsPage;
